import React, { useEffect, useState } from "react";
import client, { formatApiError } from "@/lib/api";
import {
  Users, GraduationCap, BookOpen, LayoutGrid, CalendarCheck, TrendingUp,
  School, Sparkles, ArrowRight,
} from "lucide-react";

const Stat = ({ value, subtext, label, icon: Icon, tile, testid }) => (
  <div className={`rounded-2xl p-5 hv-tile-${tile}`} data-testid={testid}>
    <div className="hv-tile-icon text-hv-navy">
      <Icon size={18} strokeWidth={2.5} />
    </div>
    <div className="text-3xl sm:text-4xl hv-heading text-hv-navy mt-3">{value}</div>
    {subtext && <div className="text-[11px] text-hv-slate mt-0.5">{subtext}</div>}
    <div className="text-[11px] uppercase tracking-wider text-hv-slate mt-1 font-bold">{label}</div>
  </div>
);

const FlowNode = ({ icon: Icon, count, label, tile }) => (
  <div className="flex flex-col items-center gap-2 shrink-0">
    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center hv-tile-${tile} text-hv-navy`}>
      <Icon size={22} strokeWidth={2.5} />
    </div>
    <div className="text-center">
      <div className="hv-heading text-hv-navy leading-none">{count}</div>
      <div className="text-[11px] text-hv-slate mt-1 whitespace-nowrap">{label}</div>
    </div>
  </div>
);

export default function PrincipalDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    client.get("/principal/overview")
      .then(({ data }) => setData(data))
      .catch((ex) => setErr(formatApiError(ex.response?.data?.detail) || ex.message));
  }, []);

  if (err) return <div className="hv-card p-7 text-hv-coral" data-testid="principal-overview-error">{err}</div>;
  if (!data) return <div className="p-6 text-hv-slate">Loading…</div>;

  const attendanceKnown = data.attendance_today_pct !== null && data.attendance_today_pct !== undefined;

  return (
    <div data-testid="principal-overview">
      {/* Hero school card */}
      {data.school && (
        <div className="hv-card hv-grain relative overflow-hidden p-7 flex items-center gap-5 flex-wrap">
          <div className="w-16 h-16 rounded-2xl bg-hv-cream flex items-center justify-center shrink-0">
            <School size={28} strokeWidth={2.5} className="text-hv-navy" />
          </div>
          <div>
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-hv-teal font-bold">
              <Sparkles size={12} strokeWidth={2.5} /> Your school
            </div>
            <h2 className="hv-heading mt-1 text-2xl sm:text-3xl text-hv-navy">{data.school.name}</h2>
            {data.school.city_country && <p className="text-hv-slate text-sm mt-0.5">{data.school.city_country}</p>}
          </div>
          <span className="hv-pill bg-white border-2 border-[#E5DFC8] px-4 py-2 text-xs font-bold text-hv-navy ms-auto shrink-0"
                data-testid="seats-pill">
            {data.school.seats_left} seats free of {data.school.max_learners}
          </span>
        </div>
      )}

      {/* Stat tiles */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4">
        <Stat testid="stat-teachers" value={data.teacher_count} label="Teachers" icon={GraduationCap} tile="teal" />
        <Stat testid="stat-students" value={data.student_count} label="Students" icon={Users} tile="gold" />
        <Stat testid="stat-classes" value={data.class_count} label="Classes" icon={LayoutGrid} tile="lavender" />
        <Stat testid="stat-sections" value={data.section_count} label="Sections" icon={BookOpen} tile="rose" />
        <Stat testid="stat-attendance"
              value={attendanceKnown ? `${data.attendance_today_pct}%` : "—"}
              subtext={attendanceKnown ? undefined : "Not taken yet"}
              label="Attendance today" icon={CalendarCheck} tile="coral" />
        <Stat testid="stat-progress" value={`${data.average_progress_pct}%`} label="Average progress" icon={TrendingUp} tile="teal" />
      </div>

      {/* School structure flow */}
      {data.school && (
        <div className="mt-6 hv-card p-6 sm:p-7">
          <h3 className="hv-heading text-hv-navy text-lg">School structure</h3>
          <div className="mt-5 flex items-center gap-3 sm:gap-5 overflow-x-auto pb-2">
            <FlowNode icon={School} count={1} label={data.school.name} tile="teal" />
            <ArrowRight size={18} className="text-hv-slate/50 shrink-0" />
            <FlowNode icon={GraduationCap} count={data.teacher_count} label={data.teacher_count === 1 ? "Teacher" : "Teachers"} tile="gold" />
            <ArrowRight size={18} className="text-hv-slate/50 shrink-0" />
            <FlowNode icon={LayoutGrid} count={data.class_count} label={data.class_count === 1 ? "Class" : "Classes"} tile="lavender" />
            <ArrowRight size={18} className="text-hv-slate/50 shrink-0" />
            <FlowNode icon={BookOpen} count={data.section_count} label={data.section_count === 1 ? "Section" : "Sections"} tile="rose" />
            <ArrowRight size={18} className="text-hv-slate/50 shrink-0" />
            <FlowNode icon={Users} count={data.student_count} label="Children" tile="coral" />
          </div>
        </div>
      )}
    </div>
  );
}
